import React, { useMemo, useState } from "react";
import { Popover, Box, Flex, Text } from "@mantine/core";
import { IconChevronDown, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import dayjs from "dayjs";
import useDrawerStyles from "../style";
import { QUARTERS, formatRange, matchQuarter } from "../../../pages/Home/QuarterlyReportsPage/utils";

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const buildGrid = (year, month) => {
  const first = dayjs(new Date(year, month, 1));
  const startWeekday = first.day();
  const daysInMonth = first.daysInMonth();
  const cells = [];

  for (let i = 0; i < startWeekday; i++) {
    const d = first.subtract(startWeekday - i, "day");
    cells.push({ date: d, muted: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: dayjs(new Date(year, month, d)), muted: false });
  }
  let trailing = 0;
  let next = 1;
  while (cells.length % 7 !== 0 || trailing < 0) {
    cells.push({ date: first.endOf("month").add(next, "day"), muted: true });
    next += 1;
    trailing += 1;
    if (cells.length >= 42) break;
  }
  return cells;
};

// `quarters` — the 4 preset ranges shown in the left rail. Defaults to the
// static calendar-year list; GenerateDrawer passes the live result of
// GET /quarter-presets instead once loaded, so the presets actually reflect
// the "Quarter basis" setting (calendar vs. ISP-plan-year anchored) rather
// than always being plain calendar quarters regardless of that setting.
const RangePickerField = ({ value, onChange, quarters = QUARTERS }) => {
  const { classes, cx } = useDrawerStyles();
  const [opened, setOpened] = useState(false);
  const [calView, setCalView] = useState(() => {
    const d = dayjs(value.from);
    return { y: d.year(), m: d.month() };
  });
  const [rangePending, setRangePending] = useState(false);
  const [activePreset, setActivePreset] = useState(() => {
    const idx = matchQuarter(value.from, value.to, quarters);
    return idx === -1 ? "custom" : idx;
  });

  const triggerLabel = useMemo(() => {
    const range = formatRange(value.from, value.to);
    if (typeof activePreset === "number" && quarters[activePreset]) {
      return `${quarters[activePreset].label} · ${range}`;
    }
    return `Custom · ${range}`;
  }, [value, activePreset, quarters]);

  const grid = useMemo(() => buildGrid(calView.y, calView.m), [calView]);

  const open = () => {
    const d = dayjs(value.from);
    setCalView({ y: d.year(), m: d.month() });
    setRangePending(false);
    setOpened((o) => !o);
  };

  const pickPreset = (idx) => {
    if (idx === "custom") {
      setActivePreset("custom");
      return;
    }
    const q = quarters[idx];
    setActivePreset(idx);
    const d = dayjs(q.from);
    setCalView({ y: d.year(), m: d.month() });
    setRangePending(false);
    onChange({ from: q.from, to: q.to });
  };

  const moveCal = (step) => {
    setCalView((v) => {
      let m = v.m + step;
      let y = v.y;
      if (m < 0) {
        m = 11;
        y -= 1;
      } else if (m > 11) {
        m = 0;
        y += 1;
      }
      return { y, m };
    });
  };

  const pickDay = (date) => {
    let next;
    if (!rangePending) {
      next = { from: date.format("YYYY-MM-DD"), to: date.format("YYYY-MM-DD") };
      setRangePending(true);
    } else {
      const anchor = dayjs(value.from);
      if (date.isBefore(anchor, "day")) {
        next = { from: date.format("YYYY-MM-DD"), to: anchor.format("YYYY-MM-DD") };
      } else {
        next = { from: anchor.format("YYYY-MM-DD"), to: date.format("YYYY-MM-DD") };
      }
      setRangePending(false);
    }
    const idx = matchQuarter(next.from, next.to, quarters);
    setActivePreset(idx === -1 ? "custom" : idx);
    onChange(next);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="top-start"
      withinPortal
      shadow="md"
      zIndex={1200}
    >
      <Popover.Target>
        <button type="button" className={classes.select} onClick={open}>
          <span>{triggerLabel}</span>
          <IconChevronDown size={15} color="#9CA3AF" />
        </button>
      </Popover.Target>
      <Popover.Dropdown className={classes.rangePop} p={0}>
        <Box className={classes.rpPresets}>
          <Text className={classes.rpPresetsLbl}>Quarters</Text>
          {quarters.map((q, idx) => (
            <button
              key={q.label}
              type="button"
              className={cx(classes.rpPreset, activePreset === idx && classes.rpPresetOn)}
              onClick={() => pickPreset(idx)}
            >
              {q.label}
              <span className={classes.rpPresetSub}>{q.sub}</span>
            </button>
          ))}
          <button
            type="button"
            className={cx(classes.rpPreset, activePreset === "custom" && classes.rpPresetOn)}
            onClick={() => pickPreset("custom")}
          >
            Custom range
          </button>
        </Box>
        <Box className={classes.rpMain}>
          <Box className={classes.rpCal}>
            <Flex className={classes.calHead}>
              <button type="button" className={classes.calNav} onClick={() => moveCal(-1)}>
                <IconChevronLeft size={14} />
              </button>
              <Text className={classes.calTitle}>{dayjs(new Date(calView.y, calView.m, 1)).format("MMMM YYYY")}</Text>
              <button type="button" className={classes.calNav} onClick={() => moveCal(1)}>
                <IconChevronRight size={14} />
              </button>
            </Flex>
            <Box className={classes.calGrid}>
              {DOW.map((d) => (
                <Text key={d} className={classes.calDow}>
                  {d}
                </Text>
              ))}
              {grid.map((cell, i) => {
                const isStart = cell.date.isSame(dayjs(value.from), "day");
                const isEnd = cell.date.isSame(dayjs(value.to), "day");
                const inRange =
                  !cell.muted &&
                  cell.date.isAfter(dayjs(value.from), "day") &&
                  cell.date.isBefore(dayjs(value.to), "day");
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={cell.muted}
                    className={cx(
                      classes.calDay,
                      cell.muted && classes.calDayMuted,
                      inRange && classes.calDayInRange,
                      (isStart || isEnd) && !cell.muted && classes.calDayEdge
                    )}
                    onClick={() => !cell.muted && pickDay(cell.date)}
                  >
                    {cell.date.date()}
                  </button>
                );
              })}
            </Box>
          </Box>
          <Flex className={classes.rpFoot}>
            <Text className={classes.rpRangeText}>{formatRange(value.from, value.to)}</Text>
            <button type="button" className={classes.rpApply} onClick={() => setOpened(false)}>
              Apply
            </button>
          </Flex>
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
};

export default RangePickerField;
